import React, { useState, useMemo, useRef, useEffect } from "react";
import { FaUserCircle, FaSpinner } from "react-icons/fa";
import {
  formatApptDate,
  formatMobileNumber,
  formatCurrentAgent,
  formatPatientDetails,
  formatStatusBadge,
  FormatCallStatusDetails,
} from "../../reusable-components/PageFormats";
import PageTableHeaderCols from "../../reusable-components/PageTableHeaderCols";
import PageTableStatusFilter from "../../reusable-components/PageTableStatusFilter";
import CompletedCallsPatientInfoCard from "./CompletedCalls/CompletedCallsPatientInfoCard";

export default function AICallsTable({
  PageName,
  TableData,
  TableColumns,
  selAppoint,
  onSelAppoint,
}) {
  // Determine if variant is 'completedCalls' or 'openCalls'
  const isCompleted = PageName === "CompletedCalls";

  // For completed calls, define columns for selected patient table
  const selPatColumns = isCompleted
    ? PageTableHeaderCols({ PageName: "SelCompletedCall" })
    : [];

  // Status filter keys for completed calls
  const statusFilterKeys = useMemo(() => {
    if (!isCompleted) return {};
    const filterKeys = {
      apptStatFilterKey: "PatientStatus",
    };
    return filterKeys;
  }, [isCompleted]);

  // Get unique options for status filter (completed calls)
  const uniqueApptStatOpts = useMemo(() => {
    if (!isCompleted) return {};
    const getUniqueNormalizedValues = (key) => {
      const values = TableData?.map((item) => item?.[key]) || [];
      return Array.from(
        new Set(
          values
            .filter(Boolean)
            .map((val) => val)
            .map((val) => val)
        )
      );
    };
    return {
      [statusFilterKeys.apptStatFilterKey]: getUniqueNormalizedValues(
        statusFilterKeys.apptStatFilterKey
      ),
    };
  }, [TableData, statusFilterKeys, isCompleted]);

  // Status filters state (completed calls)
  const [statusFilters, setStatusFilters] = useState(() => ({
    [statusFilterKeys.apptStatFilterKey]: new Set(), // empty Set = “All”
  }));
  const [selectedRowIndex, setSelectedRowIndex] = useState(null);

  const audioRef = useRef(null);
  const [audioPath, setAudioPath] = useState(null);
  const [playingRowId, setPlayingRowId] = useState(null);

  const handlePlayPause = (path, rowId) => {
    if (!audioRef.current) return;

    if (audioPath === path && playingRowId === rowId) {
      audioRef.current.pause();
      setPlayingRowId(null);
      setAudioPath(null);
      return;
    }

    document.querySelectorAll("audio").forEach((audioEl) => {
      if (audioEl !== audioRef.current && !audioEl.paused) {
        audioEl.pause();
      }
    });

    setAudioPath(path);
    setPlayingRowId(rowId);
    setTimeout(() => {
      audioRef.current.play().catch((err) => {
        console.error("Audio play error:", err);
        setPlayingRowId(null);
        setAudioPath(null);
      });
    }, 0);
  };

  useEffect(() => {
    const audioEl = audioRef.current;
    if (!audioEl) return;
    const onEnded = () => {
      setPlayingRowId(null);
      setAudioPath(null);
    };
    const onPaused = () => {
      setPlayingRowId(null);
      setAudioPath(null);
    };
    audioEl.addEventListener("ended", onEnded);
    audioEl.addEventListener("pause", onPaused);
    return () => {
      audioEl.removeEventListener("ended", onEnded);
      audioEl.removeEventListener("pause", onPaused);
    };
  }, []);

  // Filtered table data based on status filters (completed calls)
  const filteredTableData = useMemo(() => {
    if (!TableData?.length) return [];
    if (!isCompleted)
      return [...TableData].sort(
        (a, b) => new Date(b?.UpdatedAt) - new Date(a?.UpdatedAt)
      );
    const filtered = TableData.filter((appt) => {
      return Object.entries(statusFilters).every(([key, set]) => {
        // empty Set ➜ no restriction (i.e. “All”)
        return set.size === 0 || set.has(appt?.[key]);
      });
    });
    filtered.sort((a, b) => new Date(b?.UpdatedAt) - new Date(a?.UpdatedAt));
    return filtered;
  }, [TableData, statusFilters, isCompleted]);

  // Calculate row count and decide scroll style
  const rowCount = filteredTableData.length;
  const shouldScroll = rowCount >= 5;

  // Formatting functions for completed calls table columns
  const dataFormatRequiredCols = useMemo(() => {
    const formatMap = {
      PatientID: (val, row) =>
        formatPatientDetails(row?.PatientID, row?.PatientName),
      AppointmentDate: (val) => formatApptDate(val),
      PatientMobile: formatMobileNumber,
      AgentOverallStatus: (val, row) =>
        !isCompleted ? (
          <FaSpinner size={20} className="w-6 h-6 text-blue-500 animate-spin" />
        ) : (
          <FormatCallStatusDetails
            appointment={row}
            pageName="CompletedCalls"
            isPlaying={playingRowId === row?.PatientID}
            handlePlayPause={() =>
              handlePlayPause(row?.Recordingpath, row?.PatientID)
            }
          />
        ),
      PatientStatus: formatStatusBadge,
    };

    for (let i = 1; i <= 8; i++) {
      formatMap[`currentagentA${i}`] = formatCurrentAgent;
    }

    return formatMap;
  }, [isCompleted, playingRowId]);

  // Formatting functions for selected patient columns (completed calls)
  const selDataFormatRequiredCols = [
    useMemo(() => {
      if (!isCompleted) return {};
      const formatMap = {
        patient: (val, row, rowIndex) => (
          <div
            key={val}
            className="flex items-center gap-3 cursor-pointer"
            onClick={() => [setSelectedRowIndex(rowIndex), onSelAppoint(row)]}
          >
            <FaUserCircle size={25} />
            <div>
              <div className="font-medium">{row?.PatientName}</div>
              <div className="text-sm text-gray-600">{row?.PatientID}</div>
            </div>
          </div>
        ),
        appointment: (val, row) => (
          <>
            <div className="text-sm font-medium text-gray-900">
              {formatApptDate(row?.AppointmentDate)}
            </div>
            <div className="text-sm text-gray-900">{row?.AppointmentDay}</div>
          </>
        ),
        PatientStatus: formatStatusBadge,
      };
      return formatMap;
    }, [onSelAppoint, isCompleted]),
  ];

  // Completed calls with selected appointment panel
  if (isCompleted && selAppoint) {
    return (
      <>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-[1fr_3fr]">
          {/* Left: Full table with selected row highlighted, scrollable */}
          <div className="w-[470px] h-[calc(100vh-100px)] overflow-y-auto overflow-x-hidden px-2 py-4 p-2 bg-white rounded-lg shadow">
            <div className="flex justify-start mb-2">
              <button
                onClick={() => onSelAppoint(null)}
                className="px-3 py-2 mb-2 text-white rounded-lg bg-sky-600 hover:bg-sky-600"
              >
                ← Back to Completed Calls
              </button>
            </div>
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  {selPatColumns.map((col) => {
                    const key = Object.keys(col)[0];
                    const label = Object.values(col)[0];
                    return (
                      <th
                        key={key}
                        className="px-2 py-2 font-bold text-left text-md text-black-700"
                      >
                        {label}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredTableData.map((row, index) => (
                  <tr
                    key={index}
                    className={`${
                      index === selectedRowIndex
                        ? "bg-blue-200 hover:bg-blue-200"
                        : "hover:bg-gray-50"
                    }`}
                  >
                    {selPatColumns.map((col, colIdx) => {
                      const key = Object.keys(col)[0];
                      const formatFnMap = selDataFormatRequiredCols[0];
                      let value = row[key] || "-";
                      value =
                        formatFnMap[key] && value
                          ? formatFnMap[key](value, row, index)
                          : value;
                      return (
                        <td
                          key={colIdx}
                          className="px-2 py-3 text-sm text-left text-gray-900 whitespace-nowrap"
                        >
                          {value}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* Right: PatientInfoCard, scrollable */}
          <div className="h-[calc(100vh-100px)] overflow-auto px-2 py-4 bg-white rounded-lg shadow">
            <CompletedCallsPatientInfoCard appointment={selAppoint} />
          </div>
        </div>
      </>
    );
  }

  // Default table rendering (both completed without selAppoint and open calls)
  return TableColumns && TableData ? (
    <>
      <div className="w-full overflow-x-auto">
        <audio ref={audioRef} src={audioPath} preload="none" />
        <div className="w-full">
          <table className="w-full min-w-full divide-y divide-gray-200 table-fixed">
            <thead className="bg-gray-100 xl:sticky xl:top-0 xl:z-10">
              <tr>
                {TableColumns.map((column) => {
                  const key = Object.keys(column)[0];
                  const label = Object.values(column)[0];
                  const centerAlign = column?.columnAlign ? "center" : "left";
                  const columnFixedWidth = column?.columnWidth || "w-[120px]";
                  return (
                    <th
                      key={key}
                      scope="col"
                      className={`px-2 py-3 text-sm text-gray-900 hover:bg-gray-100 text-${centerAlign} ${columnFixedWidth}`}
                    >
                      <div
                        className={`flex flex-col items-${centerAlign} justify-${centerAlign} ${columnFixedWidth}`}
                      >
                        <span className="mb-1 leading-tight truncate">
                          {label.includes("_") ? (
                            <>
                              {label.split("_")[0]}
                              <br />
                              {label.split("_").slice(1).join(" ")}
                            </>
                          ) : (
                            label
                          )}
                        </span>
                        {Object.values(statusFilterKeys).includes(key) && (
                          <div className="min-w-[80px]">
                            <PageTableStatusFilter
                              options={uniqueApptStatOpts?.[key] || []}
                              selectedSet={statusFilters[key]}
                              onChange={(newSet) =>
                                setStatusFilters((prev) => ({
                                  ...prev,
                                  [key]: newSet,
                                }))
                              }
                            />
                          </div>
                        )}
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>
          </table>
          {/* Scrollable container for body */}
          <div
            className={`w-full ${
              shouldScroll ? "xl:max-h-[310px] xl:overflow-y-auto" : ""
            }`}
          >
            <table className="w-full min-w-full divide-y divide-gray-200 table-fixed">
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredTableData?.length === 0 ? (
                  <tr>
                    <td
                      colSpan={TableColumns?.length}
                      className="px-4 py-4 text-sm text-center text-gray-900"
                    >
                      {isCompleted ? "No Completed Calls" : "No Open Calls"}
                    </td>
                  </tr>
                ) : (
                  filteredTableData.map((row, index) => (
                    <tr
                      key={index}
                      className="hover:bg-gray-200"
                      //   className={`${isCompleted ? "cursor-pointer" : ""} ${
                      //     index === selectedRowIndex && isCompleted
                      //       ? "bg-blue-200 hover:bg-blue-200 cursor-pointer"
                      //       : "hover:bg-gray-200"
                      //   }`}
                      //   onClick={(e) => {
                      //     if (isCompleted && !e.target.closest("button")) {
                      //       setSelectedRowIndex(index);
                      //       onSelAppoint(row);
                      //     }
                      //   }}
                    >
                      {TableColumns.map((col, colIdx) => {
                        const key = Object.keys(col)[0];
                        const formatFnMap = dataFormatRequiredCols;
                        const centerAlign = col?.columnAlign
                          ? "center"
                          : "left";
                        const columnFixedWidth =
                          col?.columnWidth || "w-[120px]";
                        let value = row[key] || "-";
                        value =
                          formatFnMap[key] && value
                            ? formatFnMap[key](value, row, index)
                            : value;
                        return (
                          <td
                            key={colIdx}
                            className={`px-4 py-3 text-sm text-${centerAlign} text-gray-900 whitespace-nowrap ${columnFixedWidth}`}
                          >
                            <div
                              className={`flex items-${centerAlign} justify-${centerAlign} ${columnFixedWidth}`}
                            >
                              <span className="truncate">{value}</span>
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  ) : null;
}
